import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm';

const EquipmentList = () => {
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const f = t('fsm');
  const isRTL = lang === 'ar';
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [equipment, setEquipment] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [filters, setFilters] = useState({ search: '', category_id: '', client_id: '', status: '', page: 1 });

  const fetchEquipment = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.client_id) params.append('client_id', filters.client_id);
      if (filters.status) params.append('status', filters.status);
      params.append('page', filters.page);
      params.append('limit', '15');

      const res = await fetch(`${API}/equipment?${params}`, { headers });
      const data = await res.json();
      if (data.success) {
        setEquipment(data.data);
        setPagination(data.pagination);
      }
    } catch (err) { console.error('Error fetching equipment:', err); }
    setLoading(false);
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/equipment-categories`, { headers });
      const data = await res.json();
      if (data.success) setCategories(data.data);
    } catch (err) { console.error(err); }
  };

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/clients?limit=100`, { headers });
      const data = await res.json();
      if (data.success) setClients(data.data);
    } catch (err) { console.error(err); }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/equipment/stats`, { headers });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchCategories(); fetchClients(); fetchStats(); }, []);
  useEffect(() => { fetchEquipment(); }, [fetchEquipment]);

  const statusColors = {
    active: { bg: '#dcfce7', color: '#166534' },
    inactive: { bg: '#f3f4f6', color: '#374151' },
    maintenance: { bg: '#fef3c7', color: '#92400e' },
    retired: { bg: '#fee2e2', color: '#991b1b' }
  };

  const statusLabels = {
    active: f.active || 'Active',
    inactive: f.inactive || 'Inactive',
    maintenance: f.maintenance || 'Maintenance',
    retired: f.retired || 'Retired'
  };

  const StatCard = ({ label, value, icon, color }) => (
    <div style={{
      background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px', padding: '16px 20px', flex: '1', minWidth: '140px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: color || '#f1f5f9', marginTop: '4px' }}>{value}</div>
        </div>
        <span style={{ fontSize: '28px' }}>{icon}</span>
      </div>
    </div>
  );

  return (
    <Layout>
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>🔧 {f.equipment_registry || 'Equipment Registry'}</h1>
          <p style={{ fontSize: '14px', color: '#94a3b8', margin: '4px 0 0' }}>{f.manage_equipment || 'Manage all maintained equipment and assets'}</p>
        </div>
        <button onClick={() => navigate('/fsm/equipment/new')} style={{
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none',
          padding: '10px 20px', borderRadius: '10px', fontWeight: '600', fontSize: '14px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          + {f.add_equipment || 'Add Equipment'}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <StatCard label={f.total_equipment || 'Total Equipment'} value={stats.total_equipment} icon="🔧" />
          <StatCard label={f.clients || 'Clients'} value={stats.total_clients} icon="🏭" />
          <StatCard label={f.locations || 'Locations'} value={stats.total_locations} icon="📍" />
          <StatCard label={f.warranty_expiring || 'Warranty Expiring'} value={stats.warranty_expiring_30d} icon="⚠️" color={stats.warranty_expiring_30d > 0 ? '#fbbf24' : undefined} />
          <StatCard label={f.breakdown_alert || 'Breakdown Alert'} value={stats.breakdown_alert} icon="🚨" color={stats.breakdown_alert > 0 ? '#f87171' : undefined} />
        </div>
      )}

      {/* Filters */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px', padding: '16px', marginBottom: '16px',
        display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center'
      }}>
        <input
          type="text" placeholder={f.search_equipment || 'Search equipment...'}
          value={filters.search}
          onChange={e => setFilters(fl => ({ ...fl, search: e.target.value, page: 1 }))}
          style={{
            flex: '1', minWidth: '200px', padding: '8px 14px', borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(15, 23, 42, 0.6)',
            color: '#f1f5f9', fontSize: '14px', outline: 'none', textAlign: isRTL ? 'right' : 'left'
          }}
        />
        <select value={filters.category_id} onChange={e => setFilters(fl => ({ ...fl, category_id: e.target.value, page: 1 }))}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(15, 23, 42, 0.6)', color: '#f1f5f9', fontSize: '14px' }}>
          <option value="">{f.all_categories || 'All Categories'}</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {isRTL && c.name_ar ? c.name_ar : c.name}</option>)}
        </select>
        <select value={filters.client_id} onChange={e => setFilters(fl => ({ ...fl, client_id: e.target.value, page: 1 }))}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(15, 23, 42, 0.6)', color: '#f1f5f9', fontSize: '14px' }}>
          <option value="">{f.all_clients || 'All Clients'}</option>
          {clients.map(c => <option key={c.id} value={c.id}>{isRTL && c.name_ar ? c.name_ar : (c.name || c.company_name)}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters(fl => ({ ...fl, status: e.target.value, page: 1 }))}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(15, 23, 42, 0.6)', color: '#f1f5f9', fontSize: '14px' }}>
          <option value="">{f.all_status || 'All Status'}</option>
          <option value="active">{statusLabels.active}</option>
          <option value="inactive">{statusLabels.inactive}</option>
          <option value="maintenance">{statusLabels.maintenance}</option>
          <option value="retired">{statusLabels.retired}</option>
        </select>
      </div>

      {/* Table */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px', overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>{f.loading || 'Loading...'}</div>
        ) : equipment.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔧</div>
            <div style={{ fontSize: '16px', fontWeight: '600' }}>{f.no_equipment || 'No equipment found'}</div>
            <div style={{ fontSize: '14px', marginTop: '4px' }}>{f.add_first || 'Add your first equipment to get started'}</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                {[
                  f.qr_code || 'QR Code',
                  f.equipment || 'Equipment',
                  f.category || 'Category',
                  f.client || 'Client',
                  f.location || 'Location',
                  f.status || 'Status',
                  f.warranty || 'Warranty',
                  f.actions || 'Actions'
                ].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: isRTL ? 'right' : 'left', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {equipment.map(eq => {
                const sc = statusColors[eq.status] || statusColors.active;
                const warrantyExpired = eq.warranty_expiry && new Date(eq.warranty_expiry) < new Date();
                return (
                  <tr key={eq.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer' }}
                    onClick={() => navigate(`/fsm/equipment/${eq.id}`)}
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '12px 16px' }}>
                      <code style={{ fontSize: '12px', color: '#818cf8', background: 'rgba(99,102,241,0.15)', padding: '2px 8px', borderRadius: '4px' }}>{eq.qr_code}</code>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: '600', color: '#f1f5f9', fontSize: '14px' }}>{isRTL && eq.name_ar ? eq.name_ar : eq.name}</div>
                      {eq.brand && <div style={{ fontSize: '12px', color: '#94a3b8' }}>{eq.brand} {eq.model}</div>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: '13px', color: '#cbd5e1' }}>{eq.category_icon} {isRTL && eq.category_name_ar ? eq.category_name_ar : eq.category_name}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#cbd5e1' }}>{isRTL && eq.client_name_ar ? eq.client_name_ar : eq.client_name}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#94a3b8' }}>{(isRTL && eq.location_name_ar ? eq.location_name_ar : eq.location_name) || '-'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px', background: sc.bg, color: sc.color }}>{statusLabels[eq.status] || eq.status}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px', color: warrantyExpired ? '#f87171' : '#94a3b8' }}>
                      {eq.warranty_expiry ? new Date(eq.warranty_expiry).toLocaleDateString(isRTL ? 'ar-EG' : 'en-US') : '-'}
                      {warrantyExpired && ' ⚠️'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={e => { e.stopPropagation(); navigate(`/fsm/equipment/${eq.id}/edit`); }}
                        style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: 'none', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                        {f.edit || 'Edit'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setFilters(fl => ({ ...fl, page: p }))}
                style={{
                  padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                  background: p === pagination.page ? '#6366f1' : 'rgba(255,255,255,0.05)',
                  color: p === pagination.page ? '#fff' : '#94a3b8',
                  border: p === pagination.page ? 'none' : '1px solid rgba(255,255,255,0.1)'
                }}>{p}</button>
            ))}
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default EquipmentList;